/**
 * pulsebot/dialog.ts — the WRITE-action layer for PulseBot (Phase 1B + 2).
 *
 * Phase 1A is read-only (router.ts → handlers.ts). Phase 1B introduced the first
 * WRITE executor (create_deal) and the write architecture every later executor
 * copies; Phase 2 adds create_ticket as a PARALLEL executor following the SAME
 * pattern (trigger → slots → fixed-enum pickers → confirm → insert → audit). The
 * shared write architecture:
 *
 *   1. Trigger detection      — a keyword match STARTS a multi-turn dialog.
 *   2. Slot filling           — an ordered list of slots, collected one at a time.
 *   3. Pickers bind real IDs  — contact/owner pickers fetch live rows and bind
 *                               the selected row's UUID (never a typed-in name).
 *   4. Confirm-before-execute — NOTHING is written until the user clicks Create.
 *   5. Audit                  — on success writeAudit() logs the mutation,
 *                               mirroring the inline DealsPage create exactly.
 *
 * The dialog STATE machine (current slot + collected values) lives in the panel
 * component (PulseBotPanel.tsx). This module is the pure logic + data layer it
 * drives — no React, no component state here.
 *
 * KEY FACTS this executor encodes (from DealsPage inspection):
 *   - Deals are an inline supabase.from('crm_deals').insert(...) — no lib fn.
 *   - workspace_id MUST be stamped = the signed-in person's workspace (RLS WITH CHECK).
 *   - `value` is DECIMAL MAJOR units (e.g. 5000 = €5,000), NOT cents.
 *   - Deals link to CONTACTS (contact_id → crm_contacts), never a company.
 *   - Only `title` is truly required; stage defaults 'prospecting'; rest nullable.
 *
 * @module lib/pulsebot/dialog
 */

import { supabase } from '../supabase';
import { writeAudit } from '../audit';
import { getDefaultPipelineId } from '../pipelines';
import { getCurrentWorkspaceId, NO_WORKSPACE_MESSAGE } from '../apiClient';
import { listSlaPolicies, computeDeadlines, SLA_PRIORITIES } from '../slaPolicies';
import { createInvoice, listInvoices } from '../invoices';
import { submitLeaveRequest } from '../leave';
import { notifyLeaveSubmitted, notifyUserInviteRequested } from '../notifications';
import { fetchDepartments } from '../departments';
import type { DealStage, TicketPriority, TicketCategory, ContactType, LeaveType, UserRole } from '../../types';
import { formatMoney } from '../format';

// ─── Dialog commands ──────────────────────────────────────────────────────────
//
// Control words the panel checks against an active dialog's free-text input.
// Matched as the WHOLE trimmed input only (case-insensitive) — NEVER as a
// substring, so a real value that merely contains the word ("CancelMe",
// "Acme Cancel Ltd", "stopwatch") is treated as normal slot text, not a command.

/** Words that abort an active dialog when typed on their own. */
export const CANCEL_WORDS = ['cancel', 'nevermind', 'never mind', 'abort', 'quit', 'stop'];

/**
 * True only when the ENTIRE trimmed input (case-insensitive) is a cancel word.
 * Substring matches are intentionally NOT cancels.
 */
export function isCancelInput(text: string): boolean {
  return CANCEL_WORDS.includes(text.trim().toLowerCase());
}

// ─── create_deal trigger ──────────────────────────────────────────────────────

/** Phrases that START the create_deal dialog (lowercased substring match). */
export const CREATE_DEAL_KEYWORDS = [
  'create deal', 'new deal', 'add deal', 'make a deal', 'create a deal',
];

/** True when the input should start the create_deal dialog. */
export function isCreateDealTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return CREATE_DEAL_KEYWORDS.some(k => q.includes(k));
}

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the create_deal dialog walks through. */
export type DealSlot = 'title' | 'value' | 'stage' | 'contact' | 'owner' | 'confirm';

export const DEAL_SLOT_ORDER: DealSlot[] = [
  'title', 'value', 'stage', 'contact', 'owner', 'confirm',
];

/**
 * Values collected as the dialog progresses. Pickers store BOTH the bound id
 * (for the insert) AND a human label (for the confirm summary + chat echo).
 */
export interface CollectedDeal {
  title?: string;
  value?: number | null;
  stage?: DealStage;
  contact_id?: string | null;
  contact_label?: string | null;
  owner_id?: string | null;
  owner_label?: string | null;
}

// ─── Stage picker options ──────────────────────────────────────────────────────

export interface StageOption { value: DealStage; label: string; }

/** The six enum-backed deal stages (matches DealsPage STAGE_ORDER / STAGE_LABELS). */
export const STAGE_OPTIONS: StageOption[] = [
  { value: 'prospecting',   label: 'Prospecting' },
  { value: 'qualification', label: 'Qualification' },
  { value: 'proposal',      label: 'Proposal' },
  { value: 'negotiation',   label: 'Negotiation' },
  { value: 'closed_won',    label: 'Closed Won' },
  { value: 'closed_lost',   label: 'Closed Lost' },
];

/** Default stage when the user doesn't pick another (matches the create form). */
export const DEFAULT_STAGE: DealStage = 'prospecting';

/** Human label for a stage value (falls back to the raw value). */
export function stageLabel(stage: DealStage | undefined): string {
  return STAGE_OPTIONS.find(s => s.value === (stage ?? DEFAULT_STAGE))?.label
    ?? STAGE_OPTIONS[0].label;
}

// ─── Picker data sources ───────────────────────────────────────────────────────

/** One selectable row in a contact / owner picker. */
export interface PickerOption { id: string; label: string; sublabel?: string; }

/** Live contacts for the contact picker (mirrors DealsPage.fetchContacts). */
export async function fetchContactOptions(): Promise<PickerOption[]> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return [];
  const { data, error } = await supabase
    .from('crm_contacts')
    .select('id, first_name, last_name, account_name')
    .eq('workspace_id', workspaceId)
    .eq('is_deleted', false)
    .order('first_name');
  if (error) {
    console.error('[PulseBot] fetchContactOptions failed', error);
    return [];
  }
  return (data ?? []).map(
    (c: { id: string; first_name: string; last_name: string; account_name: string | null }) => ({
      id: c.id,
      label: `${c.first_name} ${c.last_name}`.trim(),
      sublabel: c.account_name ?? undefined,
    }),
  );
}

/** Live profiles for the owner picker (mirrors DealsPage.fetchOwners). */
export async function fetchOwnerOptions(): Promise<PickerOption[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name')
    .order('full_name');
  if (error) {
    console.error('[PulseBot] fetchOwnerOptions failed', error);
    return [];
  }
  return (data ?? []).map(
    (p: { id: string; full_name: string }) => ({ id: p.id, label: p.full_name }),
  );
}

// ─── Confirm summary ───────────────────────────────────────────────────────────

/** Money for the confirm card. Shared engine — was a hardcoded `€` + en-US grouping. */
const formatValue = (value: number | null | undefined): string =>
  value == null ? '—' : formatMoney(value);

/** One-line summary shown in the confirm card BEFORE the write. */
export function summarizeDeal(c: CollectedDeal): string {
  return [
    `Create deal: ${c.title?.trim() || '(untitled)'}`,
    `${formatValue(c.value)} EUR`,
    stageLabel(c.stage),
    `contact: ${c.contact_label || 'None'}`,
    `owner: ${c.owner_label || 'None'}`,
  ].join(' · ');
}

// ─── Executor ──────────────────────────────────────────────────────────────────

export interface CreateDealResult {
  ok: boolean;
  dealId?: string;
  error?: string;
}

/**
 * Execute the create_deal write. Mirrors the inline insert in DealsPage exactly:
 *   - workspace_id is STAMPED to the signed-in person's workspace (RLS WITH CHECK requires it)
 *   - value is decimal MAJOR units (not cents)
 *   - currency defaults to 'EUR'
 *   - is_deleted: false
 * On success, fires writeAudit (fire-and-forget, never throws) just like the page.
 *
 * Returns a plain result object — the panel turns it into a chat reply. This fn
 * never throws for an insert error (it returns { ok:false, error }); only truly
 * unexpected throws bubble, and the panel guards those too.
 */
export async function executeCreateDeal(c: CollectedDeal): Promise<CreateDealResult> {
  const title = (c.title ?? '').trim();
  if (!title) return { ok: false, error: 'A title is required.' };

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };

  const stage = c.stage ?? DEFAULT_STAGE;
  const value = c.value ?? null;
  // Scope to the default deal pipeline (migration 0074) so the new deal shows on
  // the board, which filters by pipeline_id. Best-effort: null if none resolves.
  const pipeline_id = await getDefaultPipelineId('crm_deals');

  const { data: created, error } = await supabase
    .from('crm_deals')
    .insert({
      workspace_id: workspaceId,
      title,
      stage,
      pipeline_id,
      value,
      currency: 'EUR',
      contact_id: c.contact_id ?? null,
      owner_id: c.owner_id ?? null,
      is_deleted: false,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[PulseBot] executeCreateDeal insert failed', error);
    return { ok: false, error: error.message };
  }

  // Audit mirrors DealsPage exactly: fire-and-forget, non-fatal.
  void writeAudit({
    resource_type: 'crm_deals',
    action: 'created',
    resource_id: created?.id,
    metadata: { title, value, stage },
  });

  return { ok: true, dealId: created?.id };
}

// ════════════════════════════════════════════════════════════════════════════
// create_ticket (Phase 2) — a PARALLEL executor following the create_deal shape.
//
// Tickets DIFFER from deals (see MyTicketsPage.handleCreate):
//   - SLA deadlines are computed + stamped at create time. We REUSE the exported
//     slaPolicies helpers (listSlaPolicies + computeDeadlines) — never reimplement.
//   - workspace_id IS stamped here. The page omits it (a latent bug: NULL-workspace
//     tickets are hidden from the workspace-scoped queue); stamping fixes that.
//   - ticket_number is DB-generated — omitted (no client-side TKT-XXX logic).
//   - status is stamped 'new'; requester_id = the current user; assignee_id null
//     (v1 leaves tickets unassigned — no round-robin); source 'pulsebot'.
// ════════════════════════════════════════════════════════════════════════════

/**
 * Phrases that START the create_ticket dialog (lowercased substring match).
 *
 * Deliberately requires an explicit create verb (create / new / raise / log /
 * submit) OR the article form "open A ticket" / "open a new ticket". Bare
 * "open ticket" / "open tickets" is EXCLUDED so it can't capture the READ phrase
 * "my open tickets" — that must route to the myOpenTickets read handler.
 */
export const CREATE_TICKET_KEYWORDS = [
  'create ticket', 'create a ticket',
  'new ticket',
  'raise ticket', 'raise a ticket',
  'log a ticket', 'submit a ticket',
  'open a ticket', 'open a new ticket',
];

/** True when the input should start the create_ticket dialog. */
export function isCreateTicketTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return CREATE_TICKET_KEYWORDS.some(k => q.includes(k));
}

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the create_ticket dialog walks through. */
export type TicketSlot = 'title' | 'description' | 'priority' | 'category' | 'confirm';

export const TICKET_SLOT_ORDER: TicketSlot[] = [
  'title', 'description', 'priority', 'category', 'confirm',
];

/** Values collected as the create_ticket dialog progresses. */
export interface CollectedTicket {
  title?: string;
  description?: string | null;
  priority?: TicketPriority;
  category?: TicketCategory;
}

// ─── Priority picker options ───────────────────────────────────────────────────

export interface PriorityOption { value: TicketPriority; label: string; }

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low',
};

/** Priority buttons in the canonical SLA order (reuses SLA_PRIORITIES). */
export const PRIORITY_OPTIONS: PriorityOption[] = SLA_PRIORITIES.map(
  p => ({ value: p, label: PRIORITY_LABELS[p] }),
);

/** Default priority when the user doesn't pick another (matches the create form). */
export const DEFAULT_PRIORITY: TicketPriority = 'medium';

/** Human label for a priority value. */
export function priorityLabel(p: TicketPriority | undefined): string {
  return PRIORITY_LABELS[p ?? DEFAULT_PRIORITY];
}

// ─── Category picker options ───────────────────────────────────────────────────

export interface CategoryOption { value: TicketCategory; label: string; }

/** Category buttons (matches MyTicketsPage CATEGORY_OPTIONS). */
export const CATEGORY_OPTIONS: CategoryOption[] = [
  { value: 'it',      label: 'IT'         },
  { value: 'hr',      label: 'HR'         },
  { value: 'finance', label: 'Finance'    },
  { value: 'ops',     label: 'Operations' },
  { value: 'general', label: 'General'    },
];

/** Default category when the user doesn't pick another (matches the create form). */
export const DEFAULT_CATEGORY: TicketCategory = 'general';

/** Human label for a category value (falls back to the last option). */
export function categoryLabel(c: TicketCategory | undefined): string {
  return CATEGORY_OPTIONS.find(o => o.value === (c ?? DEFAULT_CATEGORY))?.label
    ?? CATEGORY_OPTIONS[CATEGORY_OPTIONS.length - 1].label;
}

// ─── Confirm summary ───────────────────────────────────────────────────────────

/** One-line summary (+ optional description preview) for the confirm card. */
export function summarizeTicket(c: CollectedTicket): string {
  const main = [
    `Create ticket: ${c.title?.trim() || '(untitled)'}`,
    priorityLabel(c.priority),
    categoryLabel(c.category),
  ].join(' · ');
  const desc = c.description?.trim();
  if (!desc) return main;
  const preview = desc.length > 100 ? `${desc.slice(0, 100)}…` : desc;
  return `${main}\n“${preview}”`;
}

// ─── Executor ──────────────────────────────────────────────────────────────────

export interface CreateTicketResult {
  ok: boolean;
  ticketId?: string;
  error?: string;
}

/**
 * Execute the create_ticket write. Mirrors MyTicketsPage.handleCreate's Supabase
 * insert, with two intentional choices:
 *   - workspace_id is STAMPED (the page omits it; stamping makes the ticket
 *     visible in the workspace-scoped queue — a fix, not a regression).
 *   - SLA deadlines are computed via the SHARED slaPolicies helpers so bot
 *     tickets get the exact same windows as page-created ones.
 * ticket_number is DB-generated (omitted); status 'new'; assignee_id null (v1);
 * requesterId is the current user (panel passes profile.id) — guarded here too.
 */
export async function executeCreateTicket(
  c: CollectedTicket,
  requesterId: string | null,
): Promise<CreateTicketResult> {
  const title = (c.title ?? '').trim();
  if (!title) return { ok: false, error: 'A title is required.' };
  if (!requesterId) return { ok: false, error: 'You must be signed in to create a ticket.' };

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };

  const priority = c.priority ?? DEFAULT_PRIORITY;
  const category = c.category ?? DEFAULT_CATEGORY;

  // ★ SLA deadlines — replicate the create path EXACTLY by reusing its helpers.
  const now = new Date().toISOString();
  const map = await listSlaPolicies();
  const deadlines = computeDeadlines(now, priority, map);

  const { data: created, error } = await supabase
    .from('support_tickets')
    .insert({
      workspace_id: workspaceId,
      title,
      description: c.description ?? null,
      category,
      priority,
      status: 'new',
      source: 'pulsebot',
      requester_id: requesterId,
      assignee_id: null,
      ...deadlines,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[PulseBot] executeCreateTicket insert failed', error);
    return { ok: false, error: error.message };
  }

  void writeAudit({
    resource_type: 'support_tickets',
    action: 'created',
    resource_id: created?.id,
    metadata: { title, priority, category },
  });

  return { ok: true, ticketId: created?.id };
}

// ════════════════════════════════════════════════════════════════════════════
// create_invoice (Phase 2) — a PARALLEL executor where the LIB does the heavy
// lifting. createInvoice() (lib/invoices.ts) stamps workspace_id, computes
// subtotal/tax/total, defaults status='draft', AND calls writeAudit internally.
// So this executor must NOT stamp workspace_id, NOT compute amounts, NOT audit.
//
// What the bot DOES own:
//   - invoice_number: the lib requires it but doesn't generate it. We replicate
//     InvoicesPage.generateInvoiceNumber (client-side max+1, INV-XXXX).
//   - line item: there is no amount-only invoice — amount derives from
//     line_items, so we build ONE line { description, quantity: 1, unit_price }.
//   - tax_rate: 0 so the entered amount IS the total (omitting it would add the
//     18% workspace default on top — surprising for a bot).
// ════════════════════════════════════════════════════════════════════════════

/** Phrases that START the create_invoice dialog (lowercased substring match).
 *
 * Requires an explicit create verb — NEVER bare 'invoice'/'invoices', which is
 * the overdueInvoices READ keyword (same collision lesson as create_ticket). */
export const CREATE_INVOICE_KEYWORDS = [
  'create invoice', 'create an invoice',
  'new invoice',
  'raise invoice', 'raise an invoice',
  'make an invoice',
  'generate invoice',
  'bill a client',
];

/** True when the input should start the create_invoice dialog. */
export function isCreateInvoiceTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return CREATE_INVOICE_KEYWORDS.some(k => q.includes(k));
}

// Role gate for create_invoice moved to pulsebot/permissions.ts (canCreateInvoice,
// FINANCE_ROLES) — the shared home for ALL read + write gates, built on the
// fail-closed hasAnyRole() primitive. The panel imports canCreateInvoice there.

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the create_invoice dialog walks through (all free input). */
export type InvoiceSlot = 'client_name' | 'amount' | 'due_date' | 'confirm';

export const INVOICE_SLOT_ORDER: InvoiceSlot[] = [
  'client_name', 'amount', 'due_date', 'confirm',
];

/** Values collected as the create_invoice dialog progresses. */
export interface CollectedInvoice {
  client_name?: string;
  amount?: number;
  due_date?: string; // YYYY-MM-DD
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Today as YYYY-MM-DD (local), matching InvoicesPage's issue_date default. */
export function todayISODate(): string {
  return new Date().toISOString().split('T')[0];
}

/** Today + `days`, as YYYY-MM-DD. Used for the due-date default (skip → +30d). */
export function addDaysISODate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

/**
 * Next invoice number — replicates InvoicesPage.generateInvoiceNumber (a private
 * fn there): max numeric suffix across existing invoice_numbers + 1, padded to 4
 * ('INV-0001' when none). Client-side max+1 (inherent race) — mirrors the page.
 */
export function nextInvoiceNumber(existing: { invoice_number: string }[]): string {
  if (existing.length === 0) return 'INV-0001';
  const nums = existing
    .map(inv => parseInt(inv.invoice_number.replace(/\D/g, ''), 10))
    .filter(n => !Number.isNaN(n));
  const max = nums.length > 0 ? Math.max(...nums) : 0;
  return `INV-${String(max + 1).padStart(4, '0')}`;
}

/** Format the amount for the confirm card (currency is a workspace display concern). */
function formatInvoiceAmount(amount: number | undefined): string {
  if (amount == null) return '—';
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** One-line summary shown in the confirm card BEFORE the write. */
export function summarizeInvoice(c: CollectedInvoice): string {
  return [
    `Create invoice: ${c.client_name?.trim() || '(no client)'}`,
    formatInvoiceAmount(c.amount),
    `due ${c.due_date || '—'}`,
  ].join(' · ');
}

// ─── Executor ──────────────────────────────────────────────────────────────────

export interface CreateInvoiceResult {
  ok: boolean;
  invoiceNumber?: string;
  amount?: number;
  error?: string;
}

/**
 * Execute the create_invoice write by calling the REAL createInvoice lib. The lib
 * owns workspace_id stamping, amount/tax computation, status default, and audit —
 * we only generate invoice_number, build the single line item, and pass tax_rate:0
 * (so the entered amount is the grand total). createInvoice throws on failure, so
 * we catch and return a plain result the panel turns into a chat reply.
 */
export async function executeCreateInvoice(c: CollectedInvoice): Promise<CreateInvoiceResult> {
  const client_name = (c.client_name ?? '').trim();
  if (!client_name) return { ok: false, error: 'A client name is required.' };
  if (c.amount == null || !Number.isFinite(c.amount) || c.amount <= 0) {
    return { ok: false, error: 'A positive amount is required.' };
  }

  const due_date = c.due_date || addDaysISODate(30);
  const issue_date = todayISODate();

  try {
    const existing = await listInvoices();
    const invoice_number = nextInvoiceNumber(existing);

    // The lib derives amount from line_items; with tax_rate:0 the total === amount.
    const inv = await createInvoice({
      invoice_number,
      client_name,
      issue_date,
      due_date,
      line_items: [{ description: `Invoice for ${client_name}`, quantity: 1, unit_price: c.amount }],
      tax_rate: 0,
    });
    // NOTE: createInvoice already fired writeAudit internally — do not double-log.
    return { ok: true, invoiceNumber: inv.invoice_number, amount: inv.amount };
  } catch (err) {
    console.error('[PulseBot] executeCreateInvoice failed', err);
    return { ok: false, error: err instanceof Error ? err.message : 'unknown error' };
  }
}

// ════════════════════════════════════════════════════════════════════════════
// create_company (Phase 2) — the SIMPLEST executor: 2 slots, no role gate.
//
// Like deal/ticket it's an inline insert (no lib), so the bot stamps
// workspace_id AND calls writeAudit itself (nothing else logs companies — no
// double-log). Only `name` is required; `industry` is a UI-driven enum (default
// 'Technology'); every other column is left null / DB-defaulted.
// ════════════════════════════════════════════════════════════════════════════

/** Phrases that START the create_company dialog (lowercased substring match).
 *  'company' appears in no read/write keyword — no collision. */
export const CREATE_COMPANY_KEYWORDS = [
  'create company', 'new company',
  'add company', 'add a company',
  'register a company', 'register company',
];

/** True when the input should start the create_company dialog. */
export function isCreateCompanyTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return CREATE_COMPANY_KEYWORDS.some(k => q.includes(k));
}

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the create_company dialog walks through. */
export type CompanySlot = 'name' | 'industry' | 'confirm';

export const COMPANY_SLOT_ORDER: CompanySlot[] = ['name', 'industry', 'confirm'];

/** Industry enum (mirrors CRMCompaniesPage's UI dropdown; stored as text). */
export type CompanyIndustry =
  | 'Technology' | 'Finance' | 'Healthcare' | 'Retail'
  | 'Manufacturing' | 'Services' | 'Other';

/** Values collected as the create_company dialog progresses. */
export interface CollectedCompany {
  name?: string;
  industry?: CompanyIndustry;
}

// ─── Industry picker options ────────────────────────────────────────────────

export interface IndustryOption { value: CompanyIndustry; label: string; }

/** Industry buttons (label === value, matching CRMCompaniesPage INDUSTRY_OPTIONS). */
export const INDUSTRY_OPTIONS: IndustryOption[] = [
  { value: 'Technology',    label: 'Technology' },
  { value: 'Finance',       label: 'Finance' },
  { value: 'Healthcare',    label: 'Healthcare' },
  { value: 'Retail',        label: 'Retail' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Services',      label: 'Services' },
  { value: 'Other',         label: 'Other' },
];

/** Default industry when the user doesn't pick another (matches the create form). */
export const DEFAULT_INDUSTRY: CompanyIndustry = 'Technology';

// ─── Confirm summary ───────────────────────────────────────────────────────────

/** One-line summary shown in the confirm card BEFORE the write. */
export function summarizeCompany(c: CollectedCompany): string {
  return [
    `Create company: ${c.name?.trim() || '(unnamed)'}`,
    c.industry ?? DEFAULT_INDUSTRY,
  ].join(' · ');
}

// ─── Executor ──────────────────────────────────────────────────────────────────

export interface CreateCompanyResult {
  ok: boolean;
  companyId?: string;
  error?: string;
}

/**
 * Execute the create_company write. Mirrors CRMCompaniesPage's inline insert:
 * stamps workspace_id, sends name + industry, leaves every other column null
 * (DB defaults is_deleted/timestamps), and fires writeAudit itself. No role gate
 * (the page/route is open to all authenticated users).
 */
export async function executeCreateCompany(c: CollectedCompany): Promise<CreateCompanyResult> {
  const name = (c.name ?? '').trim();
  if (!name) return { ok: false, error: 'A company name is required.' };

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };

  const industry = c.industry ?? DEFAULT_INDUSTRY;

  const { data: created, error } = await supabase
    .from('crm_companies')
    .insert({
      workspace_id: workspaceId,
      name,
      industry,
      website: null,
      phone: null,
      email: null,
      address: null,
      employee_count: null,
      annual_revenue: null,
      notes: null,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[PulseBot] executeCreateCompany insert failed', error);
    return { ok: false, error: error.message };
  }

  void writeAudit({
    resource_type: 'crm_companies',
    action: 'created',
    resource_id: created?.id,
    metadata: { name },
  });

  return { ok: true, companyId: created?.id };
}

// ════════════════════════════════════════════════════════════════════════════
// add_contact (Phase 2) — twin of create_company, but with TWO required name
// slots. Inline insert (no lib): the bot stamps workspace_id AND calls writeAudit
// itself. account_name is free text (not a company FK) so it's omitted from the
// minimal dialog (null). No role gate (/crm/contacts is open to all).
// ════════════════════════════════════════════════════════════════════════════

/** Phrases that START the add_contact dialog (lowercased substring match).
 *  'contact' appears in no read/write keyword — no collision. */
export const CREATE_CONTACT_KEYWORDS = [
  'add contact', 'add a contact',
  'new contact',
  'create contact', 'create a contact',
];

/** True when the input should start the add_contact dialog. */
export function isAddContactTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return CREATE_CONTACT_KEYWORDS.some(k => q.includes(k));
}

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the add_contact dialog walks through. */
export type ContactSlot = 'first_name' | 'last_name' | 'type' | 'confirm';

export const CONTACT_SLOT_ORDER: ContactSlot[] = ['first_name', 'last_name', 'type', 'confirm'];

/** Values collected as the add_contact dialog progresses. */
export interface CollectedContact {
  first_name?: string;
  last_name?: string;
  type?: ContactType;
}

// ─── Type picker options ────────────────────────────────────────────────────

export interface ContactTypeOption { value: ContactType; label: string; }

/** Contact-type buttons (matches ContactsPage TYPE_OPTIONS / TYPE_LABEL). */
export const CONTACT_TYPE_OPTIONS: ContactTypeOption[] = [
  { value: 'lead',     label: 'Lead' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'client',   label: 'Client' },
  { value: 'partner',  label: 'Partner' },
  { value: 'vendor',   label: 'Vendor' },
];

/** Default contact type when the user doesn't pick another (matches the form). */
export const DEFAULT_CONTACT_TYPE: ContactType = 'lead';

/** Human label for a contact type (falls back to the default's label). */
export function contactTypeLabel(t: ContactType | undefined): string {
  return CONTACT_TYPE_OPTIONS.find(o => o.value === (t ?? DEFAULT_CONTACT_TYPE))?.label
    ?? CONTACT_TYPE_OPTIONS[0].label;
}

// ─── Confirm summary ───────────────────────────────────────────────────────────

/** Full name from the collected first/last (trimmed, single-spaced). */
function contactFullName(c: CollectedContact): string {
  return `${c.first_name?.trim() ?? ''} ${c.last_name?.trim() ?? ''}`.trim();
}

/** One-line summary shown in the confirm card BEFORE the write. */
export function summarizeContact(c: CollectedContact): string {
  return [
    `Add contact: ${contactFullName(c) || '(no name)'}`,
    contactTypeLabel(c.type),
  ].join(' · ');
}

// ─── Executor ──────────────────────────────────────────────────────────────────

export interface AddContactResult {
  ok: boolean;
  contactId?: string;
  name?: string;
  error?: string;
}

/**
 * Execute the add_contact write. Mirrors ContactsPage's inline insert: stamps
 * workspace_id, sends type + first_name + last_name, leaves every other column
 * null / tags [] (DB defaults is_deleted/timestamps), and fires writeAudit
 * itself. No role gate (the page/route is open to all authenticated users).
 */
export async function executeAddContact(c: CollectedContact): Promise<AddContactResult> {
  const first_name = (c.first_name ?? '').trim();
  const last_name = (c.last_name ?? '').trim();
  if (!first_name || !last_name) {
    return { ok: false, error: 'First and last name are required.' };
  }

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };

  const type = c.type ?? DEFAULT_CONTACT_TYPE;
  const name = `${first_name} ${last_name}`.trim();

  const { data: created, error } = await supabase
    .from('crm_contacts')
    .insert({
      workspace_id: workspaceId,
      type,
      first_name,
      last_name,
      email: null,
      phone: null,
      account_name: null,
      job_title: null,
      website: null,
      city: null,
      country: null,
      owner_id: null,
      tags: [],
      notes: null,
      is_deleted: false,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[PulseBot] executeAddContact insert failed', error);
    return { ok: false, error: error.message };
  }

  void writeAudit({
    resource_type: 'crm_contacts',
    action: 'created',
    resource_id: created?.id,
    metadata: { name },
  });

  return { ok: true, contactId: created?.id, name };
}

// ════════════════════════════════════════════════════════════════════════════
// request_leave (Phase 2) — the meatiest executor. Self-service, employee-scoped.
//
// Differs from the others:
//   - USES THE LIB submitLeaveRequest() (dual-path, fires the leave.submitted
//     automation, stamps status:'pending', returns the new id).
//   - employee_id = the current user's profile.id (the person IS the profile).
//   - NO workspace_id (leave is employee-scoped, not workspace-stamped).
//   - `days` is COMPUTED from the date range (working days, Mon–Fri).
//   - The lib does NOT audit or notify, so this executor does both itself: a
//     writeAudit and a manager notification (so the request enters the approval
//     flow). No role gate (self-service); no balance check (MVP).
// ════════════════════════════════════════════════════════════════════════════

/** Phrases that START the request_leave dialog (lowercased substring match).
 *  'leave' / 'time off' / 'holiday' / 'day off' appear in no read/write keyword. */
export const REQUEST_LEAVE_KEYWORDS = [
  'request leave', 'book leave', 'take leave',
  'request time off', 'book time off',
  'apply for leave',
  'request a holiday', 'request a day off',
];

/** True when the input should start the request_leave dialog. */
export function isRequestLeaveTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return REQUEST_LEAVE_KEYWORDS.some(k => q.includes(k));
}

// ─── Working-days helper ──────────────────────────────────────────────────────

/**
 * Inclusive count of weekdays (Mon–Fri) between two ISO dates, excluding Sat/Sun.
 * Replicates MyLeavePage.workingDays. Returns 0 if a date is missing or start>end.
 */
export function workingDays(start: string, end: string): number {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || s > e) return 0;
  let count = 0;
  const cur = new Date(s);
  while (cur <= e) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/** True when an ISO date string parses to a real date. */
export function isValidISODate(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  return !Number.isNaN(new Date(t).getTime());
}

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the request_leave dialog walks through. */
export type LeaveSlot = 'leave_type' | 'start_date' | 'end_date' | 'reason' | 'confirm';

export const LEAVE_SLOT_ORDER: LeaveSlot[] = [
  'leave_type', 'start_date', 'end_date', 'reason', 'confirm',
];

/** Values collected as the request_leave dialog progresses. */
export interface CollectedLeave {
  leave_type?: LeaveType;
  start_date?: string;
  end_date?: string;
  days?: number;
  reason?: string | null;
}

// ─── Leave-type picker options ────────────────────────────────────────────────

export interface LeaveTypeOption { value: LeaveType; label: string; }

/** Leave-type buttons (matches MyLeavePage leaveTypes). */
export const LEAVE_TYPE_OPTIONS: LeaveTypeOption[] = [
  { value: 'annual',    label: 'Annual' },
  { value: 'sick',      label: 'Sick' },
  { value: 'personal',  label: 'Personal' },
  { value: 'unpaid',    label: 'Unpaid' },
  { value: 'maternity', label: 'Maternity' },
  { value: 'paternity', label: 'Paternity' },
];

/** Default leave type when the user doesn't pick another (matches the form). */
export const DEFAULT_LEAVE_TYPE: LeaveType = 'annual';

/** Human label for a leave type (falls back to the default's label). */
export function leaveTypeLabel(t: LeaveType | undefined): string {
  return LEAVE_TYPE_OPTIONS.find(o => o.value === (t ?? DEFAULT_LEAVE_TYPE))?.label
    ?? LEAVE_TYPE_OPTIONS[0].label;
}

// ─── Confirm summary ───────────────────────────────────────────────────────────

/** One-line summary (+ optional reason preview) for the confirm card. */
export function summarizeLeave(c: CollectedLeave): string {
  const days = c.days ?? 0;
  const main = [
    `Request leave: ${leaveTypeLabel(c.leave_type)}`,
    `${c.start_date || '—'} → ${c.end_date || '—'}`,
    `${days} working day${days === 1 ? '' : 's'}`,
  ].join(' · ');
  const reason = c.reason?.trim();
  if (!reason) return main;
  const preview = reason.length > 100 ? `${reason.slice(0, 100)}…` : reason;
  return `${main}\n“${preview}”`;
}

// ─── Executor ──────────────────────────────────────────────────────────────────

/** Minimal requester identity the executor needs (from BotContext.profile). */
export interface LeaveRequester {
  id: string;
  full_name: string;
  department_id: string | null;
}

export interface RequestLeaveResult {
  ok: boolean;
  leaveId?: string;
  /** True if a department head was found and notified. */
  notifiedManager?: boolean;
  error?: string;
}

/**
 * Execute the request_leave write. Calls the canonical lib submitLeaveRequest
 * (dual-path, fires the leave.submitted automation, stamps status:'pending',
 * returns the new id), then adds the two side-effects the lib omits: a writeAudit
 * and a manager notification (department head → notifyLeaveSubmitted). No
 * workspace_id (employee-scoped); employee_id = the requester's profile id.
 */
export async function executeRequestLeave(
  c: CollectedLeave,
  requester: LeaveRequester | null,
): Promise<RequestLeaveResult> {
  if (!requester) return { ok: false, error: 'You must be signed in to request leave.' };

  const leave_type = c.leave_type ?? DEFAULT_LEAVE_TYPE;
  const start_date = c.start_date ?? '';
  const end_date = c.end_date ?? '';
  const days = c.days ?? workingDays(start_date, end_date);
  if (!start_date || !end_date) return { ok: false, error: 'Start and end dates are required.' };
  if (days <= 0) return { ok: false, error: 'That date range has no working days.' };

  try {
    const res = await submitLeaveRequest({
      employee_id: requester.id,
      leave_type,
      start_date,
      end_date,
      days,
      reason: c.reason ?? undefined,
    });
    if (!res.ok || !res.id) return { ok: false, error: res.error ?? 'The leave request could not be saved.' };
    const id = res.id;

    void writeAudit({
      resource_type: 'leave_requests',
      action: 'created',
      resource_id: id,
      metadata: { leave_type, start_date, end_date, days },
    });

    // Manager notify — replicate MyLeavePage's side-effect so the request enters
    // the approval flow. Guarded: skip silently if no department / head.
    let notifiedManager = false;
    if (requester.department_id) {
      try {
        // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
        const { data: dept } = await supabase
          .from('departments')
          .select('head_id')
          .eq('id', requester.department_id)
          .single();
        const headId = (dept as { head_id?: string | null } | null)?.head_id;
        if (headId) {
          await notifyLeaveSubmitted({
            actorId: requester.id,
            actorName: requester.full_name,
            managerId: headId,
            leaveId: id,
            leaveType: leave_type,
            startDate: start_date,
          });
          notifiedManager = true;
        }
      } catch (err) {
        // Notification is best-effort — never fail the request over it.
        console.warn('[PulseBot] executeRequestLeave manager-notify failed', err);
      }
    }

    return { ok: true, leaveId: id, notifiedManager };
  } catch (err) {
    console.error('[PulseBot] executeRequestLeave failed', err);
    return { ok: false, error: err instanceof Error ? err.message : 'unknown error' };
  }
}

// ════════════════════════════════════════════════════════════════════════════
// request_invite (Phase 4, SAFE) — records a PENDING user invite. NO auth-user
// creation, NO service key: the bot inserts a user_invites row (status 'pending')
// and notifies super_admins, who complete provisioning in /admin/users.
//
// Security:
//   - STRICTEST gate (super_admin only) lives in permissions.ts (canRequestInvite);
//     the panel checks it before starting the dialog.
//   - ANTI-ESCALATION: the suggested_role picker offers ONLY the non-privileged
//     subset (no platform_owner / super_admin / admin). The DB also has a CHECK
//     constraint rejecting privileged roles as a backstop.
// ════════════════════════════════════════════════════════════════════════════

/** Phrases that START the request_invite dialog (lowercased substring match).
 *  'user'/'account'/'invite' appear in no other read/write keyword (no collision;
 *  'add user' does NOT collide with 'add contact'). NOT 'onboard' (too broad). */
export const CREATE_INVITE_KEYWORDS = [
  'invite user', 'invite a user',
  'add user', 'add a user',
  'new user',
  'request user',
  'create account', 'create an account',
];

/** True when the input should start the request_invite dialog. */
export function isRequestInviteTrigger(input: string): boolean {
  const q = input.toLowerCase().trim();
  return CREATE_INVITE_KEYWORDS.some(k => q.includes(k));
}

// ─── Slots ────────────────────────────────────────────────────────────────────

/** The ordered slots the request_invite dialog walks through. */
export type InviteSlot = 'email' | 'full_name' | 'role' | 'department' | 'confirm';

export const INVITE_SLOT_ORDER: InviteSlot[] = [
  'email', 'full_name', 'role', 'department', 'confirm',
];

/** Values collected as the request_invite dialog progresses. */
export interface CollectedInvite {
  email?: string;
  full_name?: string;
  suggested_role?: UserRole;
  department_id?: string | null;
  department_label?: string | null;
}

// ─── Suggestable-role picker (anti-escalation subset) ──────────────────────────

export interface RoleOption { value: UserRole; label: string; }

/**
 * Roles the bot may SUGGEST. Deliberately EXCLUDES the privileged tier
 * (platform_owner, super_admin, admin) so the bot can't even propose escalation;
 * the DB CHECK constraint enforces the same as a backstop.
 */
export const SUGGESTABLE_ROLE_OPTIONS: RoleOption[] = [
  { value: 'employee',      label: 'Employee' },
  { value: 'team_lead',     label: 'Team Lead' },
  { value: 'manager',       label: 'Manager' },
  { value: 'dept_admin',    label: 'Dept Admin' },
  { value: 'hr_admin',      label: 'HR Admin' },
  { value: 'finance_admin', label: 'Finance Admin' },
  { value: 'crm_agent',     label: 'CRM Agent' },
  { value: 'support_agent', label: 'Support Agent' },
  { value: 'viewer',        label: 'Viewer' },
];

/** Default suggested role when the user doesn't pick another. */
export const DEFAULT_SUGGESTED_ROLE: UserRole = 'employee';

/** Human label for a suggestable role (falls back to the raw value). */
export function suggestedRoleLabel(role: UserRole | undefined): string {
  return SUGGESTABLE_ROLE_OPTIONS.find(o => o.value === (role ?? DEFAULT_SUGGESTED_ROLE))?.label
    ?? SUGGESTABLE_ROLE_OPTIONS[0].label;
}

// ─── Email validation + department picker ──────────────────────────────────────

/** Loose email sanity check: one @, non-empty local part, a dotted domain. */
export function isLikelyEmail(text: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim());
}

/** Departments for the optional picker (reuses the departments lib). */
export async function fetchDepartmentOptions(): Promise<PickerOption[]> {
  try {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return [];
    const departments = await fetchDepartments(workspaceId);
    return departments.map(d => ({ id: d.id, label: d.name }));
  } catch (err) {
    console.error('[PulseBot] fetchDepartmentOptions failed', err);
    return [];
  }
}

// ─── Confirm summary ───────────────────────────────────────────────────────────

/** One-line summary shown in the confirm card BEFORE the write. */
export function summarizeInvite(c: CollectedInvite): string {
  const parts = [
    `Request invite: ${c.full_name?.trim() || '(no name)'} (${c.email?.trim() || 'no email'})`,
    suggestedRoleLabel(c.suggested_role),
  ];
  if (c.department_label) parts.push(`dept: ${c.department_label}`);
  return parts.join(' · ');
}

// ─── Executor ──────────────────────────────────────────────────────────────────

export interface RequestInviteResult {
  ok: boolean;
  inviteId?: string;
  error?: string;
}

/**
 * Execute the request_invite write: insert a PENDING user_invites row, audit it,
 * and best-effort notify super_admins. No auth-user creation — a super_admin
 * completes provisioning in /admin/users. requesterId = the current user
 * (panel passes profile.id, already super_admin-gated); guarded here too.
 */
export async function executeRequestInvite(
  c: CollectedInvite,
  requesterId: string | null,
): Promise<RequestInviteResult> {
  const email = (c.email ?? '').trim();
  const full_name = (c.full_name ?? '').trim();
  if (!requesterId) return { ok: false, error: 'You must be signed in to request an invite.' };
  if (!isLikelyEmail(email)) return { ok: false, error: 'A valid email is required.' };
  if (!full_name) return { ok: false, error: 'A full name is required.' };

  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };

  const suggested_role = c.suggested_role ?? DEFAULT_SUGGESTED_ROLE;

  const { data: created, error } = await supabase
    .from('user_invites')
    .insert({
      workspace_id: workspaceId,
      email,
      full_name,
      suggested_role,
      department_id: c.department_id ?? null,
      status: 'pending',
      invited_by: requesterId,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[PulseBot] executeRequestInvite insert failed', error);
    return { ok: false, error: error.message };
  }

  void writeAudit({
    resource_type: 'user_invites',
    action: 'created',
    resource_id: created?.id,
    metadata: { email, suggested_role },
  });

  // Best-effort admin notification — never fail the invite over it.
  try {
    await notifyUserInviteRequested({
      requesterId,
      inviteId: created?.id,
      email,
      fullName: full_name,
      suggestedRole: suggestedRoleLabel(suggested_role),
    });
  } catch (err) {
    console.warn('[PulseBot] executeRequestInvite notify failed', err);
  }

  return { ok: true, inviteId: created?.id };
}
